/** Multiplier: amount in `currency` × rate = amount in CAD. */
export type ExchangeRatesToCad = Record<string, number>;

const FALLBACK_USD_TO_CAD = 1.37;
const CACHE_MS = 12 * 60 * 60 * 1000;

let cachedRates: { rates: ExchangeRatesToCad; fetchedAt: number } | null = null;

const FRANKFURTER_CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'MXN', 'NZD', 'CHF', 'JPY'];

function fallbackRates(): ExchangeRatesToCad {
  const envRate = import.meta.env.VITE_USD_TO_CAD_RATE;
  const usd =
    envRate && Number.isFinite(parseFloat(envRate)) ? parseFloat(envRate) : FALLBACK_USD_TO_CAD;
  return { cad: 1, usd };
}

export async function getExchangeRatesToCad(): Promise<ExchangeRatesToCad> {
  if (cachedRates && Date.now() - cachedRates.fetchedAt < CACHE_MS) {
    return cachedRates.rates;
  }

  const rates: ExchangeRatesToCad = { ...fallbackRates() };

  try {
    await Promise.all(
      FRANKFURTER_CURRENCIES.map(async (code) => {
        const res = await fetch(`https://api.frankfurter.app/latest?from=${code}&to=CAD`);
        if (!res.ok) return;
        const data = (await res.json()) as { rates?: { CAD?: number } };
        if (typeof data.rates?.CAD === 'number' && data.rates.CAD > 0) {
          rates[code.toLowerCase()] = data.rates.CAD;
        }
      }),
    );
    cachedRates = { rates, fetchedAt: Date.now() };
    return rates;
  } catch {
    return rates;
  }
}

export function normalizeCurrencyCode(currency: string | null | undefined): string {
  return (currency || 'usd').trim().toLowerCase();
}

/** Convert a dollar amount (not cents) to CAD. */
export function convertDollarsToCad(
  amount: number,
  currency: string | null | undefined,
  rates: ExchangeRatesToCad,
): number {
  const code = normalizeCurrencyCode(currency);
  if (code === 'cad') return amount;
  const rate = rates[code] ?? rates.usd ?? FALLBACK_USD_TO_CAD;
  return amount * rate;
}

/** Convert Stripe cents to CAD cents. */
export function convertCentsToCadCents(
  cents: number,
  currency: string | null | undefined,
  rates: ExchangeRatesToCad,
): number {
  const cadDollars = convertDollarsToCad(cents / 100, currency, rates);
  return Math.round(cadDollars * 100);
}

export function formatCad(amountDollars: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: amountDollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountDollars);
}

export function formatCadCents(cents: number): string {
  return formatCad(cents / 100);
}
